import clsx from 'clsx';
import { bool, string } from 'prop-types';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Margin, usePDF } from 'react-to-pdf';

import { Icon } from 'components';
import { formatDate } from 'helpers';
import { appService } from 'services';

const frontUrl = 'www.touloisirs.fr';

const DottedText = ({ label, value = null, className = 'w-full', icon = null, helpText = null, dataTestId = null }) => (
  <div className="flex grow">
    <div className="shrink-0 flex items-end gap-2 text-sm pb-1 text-gray-600">
      {icon && <Icon name={icon} />}
      <span className={clsx(icon && 'pb-1')}>{label}</span>
      {helpText && <span className="italic">{helpText}</span>} :
    </div>
    <div className={clsx('dotted-b pb-1 pl-3 text-gray-700 uppercase', className)} data-test-id={dataTestId}>
      {value && <span>{value}</span>}
    </div>
  </div>
);

const Checkbox = ({ label, isChecked = false, dataTestId }) => (
  <div className="flex items-center gap-2">
    <div
      className="shrink-0 w-4 h-4 border border-gray-700 border-r-2 border-b-2 mt-4 flex-center-center"
      data-test-id={dataTestId}
      data-test-check={isChecked}
    >
      {isChecked && <Icon name="close" />}
    </div>
    <span className="text-sm">{label}</span>
  </div>
);

const RegistrationPdf = () => {
  const { t } = useTranslation();
  const { toPDF, targetRef } = usePDF({
    filename: 'bulletin-adhesion.pdf',
    page: { margin: Margin.MEDIUM }
  });

  const { getValues } = useFormContext();

  const birthdate = useMemo(() => {
    const date = getValues('birthdate');

    if (date) {
      return {
        day: formatDate(date, 'dd'),
        month: formatDate(date, 'MM'),
        year: formatDate(date, 'yyyy')
      };
    }
  }, [getValues('birthdate')]);

  const year = useMemo(() => {
    // Check if current date is the 31 December
    const today = new Date();
    const shouldUpdateYear = today.getMonth() === 11 && today.getDate() === 31;

    // If it is, add 1 to the current year
    return shouldUpdateYear ? today.getFullYear() + 1 : today.getFullYear();
  }, []);

  const showPdf = () => {
    /*
			PDF content to be print inside targetRef must exist in the DOM before calling toPDF()
			Otherwise, the PDF will be empty.
			The PDF is hidden in CSS by default. It is shown only when the user clicks on the print button
		*/
    targetRef.current.classList.remove('hidden');
    targetRef.current.classList.add('block');
    toPDF();
    targetRef.current.classList.remove('block');
    targetRef.current.classList.add('hidden');
  };

  const statusValue = (status) => {
    if (status === 'otherEmployee') {
      return getValues('employementStatus') === status ? getValues('userId') : null;
    } else {
      return getValues('adhesionType') === status ? getValues('userId') : null;
    }
  };

  return (
    <>
      <button type="button" className="btn uppercase mx-auto" onClick={showPdf} data-test-id="printPdfBtn">
        {t('buttons.printReport')}
      </button>

      {/* PDF Content */}
      <div className="w-[21cm] m-0 p-4 break-after-avoid-page hidden print:block" ref={targetRef}>
        <div className="flex items-start justify-between">
          <div>
            <div className="uppercase">{t('auth:registration.pdf.reportToComplete')}</div>
            <h1 className="uppercase text-3xl mb-2">
              <span className="text-primary-500">{t('auth:registration.pdf.reportTitle')}</span>
              <span className="text-yellow-500 ml-2">{year}</span>
            </h1>
            <div className="font-medium">{t('auth:registration.pdf.reportValidDate', { year })}</div>
            <div className="text-gray-600 text-sm">{t('auth:registration.pdf.reportHelp')}</div>
          </div>

          <div className="w-64 h-auto">
            <img
              src={appService.loadBrandImage('logo')}
              alt={t('images.logoAlt')}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="my-4 text-sm">
          <h2 className="text-base uppercase text-primary-500 mb-3">{t('auth:registration.pdf.contact')}</h2>

          <div className="grid grid-cols-12 gap-1">
            <div className="col-span-8 flex gap-3">
              <DottedText
                label={t('form:lastname')}
                value={getValues('lastname')}
                dataTestId="registrationPdfLastname"
              />
              <DottedText
                label={t('form:firstname')}
                value={getValues('firstname')}
                dataTestId="registrationPdfFirstname"
              />
            </div>

            <div className="col-span-4">
              <div className="flex items-end gap-1">
                <span className="shrink-0 text-gray-600">{t('form:birthdateLabel')} :</span>
                <div
                  className={clsx('dotted-b w-12 text-center', birthdate?.day && 'pb-1')}
                  data-test-id="registrationPdfBirthdateDay"
                >
                  {birthdate?.day}
                </div>
                /
                <div
                  className={clsx('dotted-b w-12 text-center', birthdate?.month && 'pb-1')}
                  data-test-id="registrationPdfBirthdateMonth"
                >
                  {birthdate?.month}
                </div>
                /
                <div
                  className={clsx('dotted-b w-12 text-center', birthdate?.year && 'pb-1')}
                  data-test-id="registrationPdfBirthdateYear"
                >
                  {birthdate?.year}
                </div>
              </div>
            </div>

            <div className="col-span-12">
              <DottedText label={t('form:address')} />
            </div>

            <div className="col-span-4">
              <DottedText label={t('form:postalCode')} />
            </div>

            <div className="col-span-8">
              <DottedText label={t('form:city')} />
            </div>

            <div className="col-span-6">
              <DottedText
                label={t('form:mobilePhone')}
                icon="phone"
                value={getValues('mobilePhone')}
                dataTestId="registrationPdfMobilePhone"
              />
            </div>

            <div className="col-span-6">
              <DottedText label={t('form:phoneShort')} icon="phone" />
            </div>

            <div className="col-span-8">
              <DottedText label={t('form:emailShort')} value={getValues('email')} dataTestId="registrationPdfEmail" />
            </div>

            <div className="col-span-8">
              <DottedText
                label={t('form:sponsorshipCode')}
                helpText={t('auth:registration.pdf.optional')}
                value={getValues('sponsorshipCode')}
                dataTestId="registrationPdfSponsorshipCode"
              />
            </div>
          </div>
        </div>

        <p className="underline mt-6 mb-2 text-sm">
          <span className="font-bold pr-1 underline">{t('auth:registration.pdf.youAre')}</span>
          {t('auth:registration.pdf.youAreHelp')}
        </p>

        <div className="flex gap-2 items-center text-sm">
          <h2 className="text-base uppercase text-primary-500">{t('auth:registration.pdf.postalGroup')}</h2>-{' '}
          <DottedText
            label={t('auth:registration.pdf.postalId')}
            className="w-64"
            value={statusValue('postalPensioner')}
            dataTestId="registrationPdfTouloisirsId"
          />
        </div>

        <Checkbox
          label={t('auth:registration.pdf.postalRetired')}
          isChecked={getValues('adhesionType') === 'postalPensioner'}
          dataTestId="registrationPdfAdhesionTypeChecked"
        />

        <p className="my-2 text-sm">{t('auth:registration.pdf.postalHelp')}</p>

        <div className="flex gap-2 items-center text-sm">
          <h2 className="text-base uppercase text-primary-500">{t('auth:registration.pdf.orangeGroup')}</h2>-{' '}
          <DottedText
            label={t('auth:registration.pdf.touloisirsId')}
            value={
              getValues('adhesionType') === 'orangeMember' || getValues('adhesionType') === 'orangePensioner'
                ? getValues('userId')
                : null
            }
            helpText={t('auth:registration.pdf.ifKnown')}
            className="w-64"
          />
        </div>

        <Checkbox
          label={t('auth:registration.pdf.orangeActive')}
          isChecked={getValues('adhesionType') === 'orangeMember'}
        />
        <Checkbox
          label={t('auth:registration.pdf.orangeRetired')}
          isChecked={getValues('adhesionType') === 'orangePensioner'}
        />

        <div className="flex gap-2 items-center text-sm">
          <h2 className="text-primary-500">
            <span className="text-base uppercase">{t('auth:registration.pdf.other')}</span>
            <span>{t('auth:registration.pdf.otherHelp')}</span>
          </h2>
          -{' '}
          <DottedText
            label={t('auth:registration.pdf.touloisirsId')}
            value={statusValue('otherEmployee')}
            helpText={t('auth:registration.pdf.ifKnown')}
            className="w-64"
          />
        </div>

        <Checkbox label="15€" isChecked={statusValue('otherEmployee')} />

        <p className="py-1 text-sm">{t('auth:registration.pdf.sworn')}</p>

        <div className="grid grid-cols-12 gap-1">
          <div className="col-span-4 flex flex-col gap-1">
            <DottedText label={t('auth:registration.pdf.done')} />

            <div className="flex items-end gap-1">
              <span className="shrink-0 text-sm">{t('auth:registration.pdf.at')} :</span>
              <div className="dotted-b w-1/3"></div>/<div className="dotted-b w-1/3"></div>/
              <div className="dotted-b w-1/3"></div>
            </div>
          </div>

          <div className="col-span-8">
            <div className="border border-gray-700 h-16 w-3/4 ml-auto p-2 pt-0 text-sm">
              {t('auth:registration.pdf.signature')} :
            </div>
          </div>

          <div className="col-span-6 mt-6">
            <div className="h-full dotted p-2 text-center text-[0.8rem]">
              <span className="text-base uppercase text-primary-500">{t('auth:registration.pdf.sendReport')}</span>

              <p className="mt-2">{t('auth:registration.pdf.sendReportDescription')}</p>

              <address className="py-2 not-italic">
                TOULOISIRS, Service adhésion <br /> 8, rue Brillat-Savarin - CS 31379 <br /> 75214 Paris Cedex 13
              </address>

              <p className="underline">{t('auth:registration.pdf.sendReportHelp')}</p>
            </div>
          </div>

          <div className="col-span-6 mt-6">
            <div className="h-full dotted p-2 pb-4 text-center flex flex-col justify-between text-[0.8rem]">
              <div className="font-bold mb-2">
                <div className="text-base uppercase text-primary-500 font-normal">
                  {t('auth:registration.pdf.renewMembership')}
                </div>
                {t('auth:registration.pdf.renewMembershipFrom')} <span className="underline">{frontUrl}</span>
              </div>

              <p>
                {t('auth:registration.pdf.renewMembershipDescription')} <span className="underline">{frontUrl}</span>
              </p>

              <p className="py-2">{t('auth:registration.pdf.renewMembershipHelp')}</p>
              <p className="underline">{t('auth:registration.pdf.renewMembershipHelp2')}</p>
            </div>
          </div>
        </div>

        <p className="text-[0.65rem] mt-2">{t('auth:registration.pdf.legalNotice', { url: frontUrl })}</p>
      </div>
    </>
  );
};

DottedText.propTypes = {
  label: string.isRequired,
  value: string,
  icon: string,
  helpText: string,
  className: string,
  dataTestId: string
};

Checkbox.propTypes = {
  label: string.isRequired,
  dataTestId: string,
  isChecked: bool
};

export default RegistrationPdf;
